import {
  COMPLIANCE_RESULT,
  COMPLIANCE_RESULT_TO_READABLE_LABEL,
} from '@/constants/businessComplianceAgent';
import { Statuses } from '@/V2/components/status/utils';

/**
 * Get the latest business compliance agent run from a list of runs
 *
 * @param {Array} runs - List of business compliance agent runs
 * @returns {Object|null} - The latest run or null if no runs exist
 */
export function getLatestBusinessComplianceAgentRun(runs) {
  return runs?.length > 0 ? runs[runs.length - 1] : null;
}

/**
 * Extract the names of beneficial owners from the latest business compliance agent run
 *
 * @param {Object|null} latestRun - The latest business compliance agent run
 * @returns {Array<string>} - List of beneficial owner full names, or an empty array if none exist
 */
export function getBeneficialOwnerNames(latestRun) {
  return (
    latestRun?.output?.beneficial_owner_checks?.map(
      (check) => check.beneficial_owner_full_name,
    ) ?? []
  );
}

export function extractValue(value, fieldName = 'value') {
  if (!value) return null;
  return typeof value === 'object' && fieldName in value
    ? value[fieldName]
    : value;
}

export function mapComplianceResultToStatus(result) {
  if (result === COMPLIANCE_RESULT.PASS) {
    return Statuses.SUCCESS;
  } else if (result === COMPLIANCE_RESULT.FAIL) {
    return Statuses.ERROR;
  } else if (result === COMPLIANCE_RESULT.INCONCLUSIVE) {
    return Statuses.WARNING;
  } else if (result === COMPLIANCE_RESULT.NOT_APPLICABLE) {
    return Statuses.NOT_APPLICABLE;
  }
  return Statuses.DISABLED;
}

export function mapComplianceResultToLabel(result) {
  return COMPLIANCE_RESULT_TO_READABLE_LABEL[result] || 'No Rating';
}

export function formatCompanyStructure(companyStructure) {
  if (!companyStructure) return '';
  const words = companyStructure.includes('_')
    ? companyStructure.split('_')
    : companyStructure.split(' ');
  return words
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

export function getCanonicalProfileObj(
  profile,
  options = { formatCompanyStructure: true },
) {
  if (!profile) {
    return {};
  }
  const output = {};

  const businessDescription = extractValue(profile.business_description);
  if (businessDescription) {
    output.business_description = businessDescription;
  }

  const businessType = extractValue(profile.business_type);
  if (businessType) {
    output.business_type = businessType;
  }

  const address = extractValue(profile.address, 'full_address');
  if (address) {
    output.company_address = address;
  }

  const phoneNumber = extractValue(profile.phone_number);
  if (phoneNumber) {
    output.phone_number = phoneNumber;
  }

  if (profile.company_website) {
    output.company_website = profile.company_website;
  }

  const sourceOfFunds = extractValue(profile.source_of_funds_claim);
  if (sourceOfFunds) {
    output.source_of_funds_claim = sourceOfFunds;
  }

  const structure = extractValue(profile.company_structure);
  if (structure) {
    output.company_structure = options.formatCompanyStructure
      ? formatCompanyStructure(structure)
      : structure;
  }

  if (profile.ownership_structure) {
    output.ownership_structure =
      profile.ownership_structure.description || profile.ownership_structure;
  }

  return output;
}
